import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Mail, Printer } from 'lucide-react';
import { Switch } from '../ui/switch';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-bank-statement`;
const LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/token-list/beth-usd.png`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n, d = 6) => (n == null ? '-' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const PRINT_CSS = `@media print { body * { visibility: hidden; } #bank-statement, #bank-statement * { visibility: visible; -webkit-print-color-adjust: exact; print-color-adjust: exact; } #bank-statement { position: absolute; left: 0; top: 0; width: 100%; } }`;

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-slate-200 text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-right break-all" data-testid={testid}>{value}</span></div>
);

const Block = ({ title, children }) => (<div><h4 className="text-xs uppercase tracking-wider text-emerald-700 mb-1">{title}</h4>{children}</div>);

const Header = ({ s }) => (
  <div className="relative flex justify-between items-start border-b-2 border-slate-900 pb-3">
    <div className="flex items-center gap-3">
      <img src={LOGO} alt="BETH USD" className="w-12 h-12 rounded-full" data-testid="statement-logo" />
      <div><p className="text-xl font-bold">BETHCOIN Exchange Market</p><p className="text-xs text-slate-500">{s.account.bank} · BETH USD (Official)</p></div>
    </div>
    <div className="text-right"><p className="font-semibold">Account Statement</p><p className="text-sm" data-testid="statement-month">{new Date(`${s.month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</p><p className="text-xs text-slate-500">{s.membership}</p></div>
  </div>
);

const Transactions = ({ rows }) => (
  <Block title="Transactions">
    {!rows.length ? <p className="text-sm text-slate-500" data-testid="statement-no-transactions">No transactions this month.</p> : (
      <table className="w-full text-sm" data-testid="statement-transactions"><thead><tr className="text-left text-slate-500"><th>Date</th><th>Description</th><th className="text-right">Amount</th><th className="text-right">Balance</th></tr></thead>
        <tbody>{rows.map(t => <tr key={t.id} className="border-t border-slate-200"><td>{t.date}</td><td>{t.desc || t.type_source}</td><td className="text-right">{money(t.amount)}</td><td className="text-right">{t.balance == null ? '' : money(t.balance)}</td></tr>)}</tbody></table>
    )}
  </Block>
);

const Statement = ({ s }) => (
  <div id="bank-statement" className="relative overflow-hidden bg-white text-slate-900 rounded-2xl p-6 space-y-5 border-[4px] border-double border-emerald-700" data-testid="bank-statement">
    <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] max-w-[80%] opacity-[0.06]" data-testid="statement-watermark" />
    <Header s={s} />
    <div className="relative grid md:grid-cols-2 gap-6">
      <Block title="Account holder">
        <Row label="Name" value={s.holder.name} testid="statement-holder-name" />
        <Row label="Address" value={s.holder.address || 'Not on file. Add it in the banner above'} testid="statement-holder-address" />
        <Row label="Email" value={s.holder.email} testid="statement-holder-email" />
      </Block>
      <Block title="Account">
        <Row label="Account number" value={s.account.account_number} testid="statement-account-number" />
        <Row label="Routing number" value={s.account.routing_number} testid="statement-routing-number" />
        <Row label="Status" value={s.account.status} testid="statement-account-status" />
      </Block>
      <Block title="Balance summary">
        <Row label="Opening balance" value={money(s.balances.opening)} testid="statement-opening" />
        <Row label="Deposits & credits" value={money(s.balances.credits)} testid="statement-credits" />
        <Row label="Withdrawals & debits" value={money(s.balances.debits)} testid="statement-debits" />
        <Row label="Closing balance" value={money(s.balances.closing)} testid="statement-closing" />
      </Block>
      <Block title={`Connected wallet · ${s.wallet.network}`}>
        <Row label="Wallet" value={<span className="font-mono text-xs">{s.wallet.address || 'None'}</span>} testid="statement-wallet-address" />
        <Row label="BETH USD (Official)" value={num(s.wallet.holdings?.beth_usd, 2)} testid="statement-wallet-beth-usd" />
        <Row label="ETH" value={num(s.wallet.holdings?.eth)} testid="statement-wallet-eth" />
        <Row label="USDC" value={num(s.wallet.holdings?.usdc, 2)} testid="statement-wallet-usdc" />
      </Block>
      {s.credit_line && <Block title="Credit line">
        <Row label="Approved" value={money(s.credit_line.approved_usd)} testid="statement-credit-approved" />
        <Row label="Used" value={money(s.credit_line.used_usd)} testid="statement-credit-used" />
        <Row label="Available" value={money(s.credit_line.available_usd)} testid="statement-credit-available" />
      </Block>}
    </div>
    <div className="relative"><Transactions rows={s.transactions} /></div>
    <div className="relative flex flex-wrap items-end justify-between gap-3 text-[11px] text-slate-500">
      <div className="space-y-1">
        <p>Generated {new Date(s.generated_at).toLocaleString()}. Wallet balances are live on-chain readings. Not FDIC insured.</p>
        <p className="font-mono" data-testid="statement-document-id">Document {s.document_id}</p>
      </div>
      <div className="text-center" data-testid="statement-qr">
        <QRCodeSVG value={`${window.location.origin}/verify-statement/${s.document_id}?t=${s.verify_token}`} size={104} level="Q" marginSize={4} bgColor="#ffffff" fgColor="#000000" />
        <p className="text-[10px] font-semibold text-slate-700">Scan to verify</p>
      </div>
    </div>
  </div>
);

const EmailToggle = ({ token }) => {
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(`${API}/email-pref`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setP(r.data)).catch(() => {}); }, [token]);
  if (!p) return null;
  const toggle = async (enabled) => {
    try { const r = await axios.put(`${API}/email-pref`, { enabled }, { headers: { Authorization: `Bearer ${token}` } }); setP({ ...p, enabled }); toast.success(r.data.message); }
    catch (e) { toast.error('Could not update'); }
  };
  return (
    <label className="flex flex-wrap items-center gap-3 text-sm text-slate-300 bg-slate-800/60 rounded-lg px-3 py-2" data-testid="statement-email-toggle">
      <Mail className="w-4 h-4 text-emerald-400" /> Email me my statement on the 1st of each month
      <Switch checked={p.enabled} onCheckedChange={toggle} className="data-[state=unchecked]:bg-slate-500" data-testid="statement-email-switch" />
      {p.test_account && <span className="text-xs text-amber-300">Test accounts never receive emails</span>}
    </label>
  );
};

export const BankStatement = ({ token, initialMonth }) => {
  const [month, setMonth] = useState(initialMonth || new Date().toISOString().slice(0, 7));
  const [s, setS] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    setErr('');
    axios.get(API, { headers: { Authorization: `Bearer ${token}` }, params: { month } }).then(r => setS(r.data)).catch(e => { setS(null); setErr(e.response?.data?.detail || 'Could not load statement'); });
  }, [token, month]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/pdf`, { headers: { Authorization: `Bearer ${token}` }, params: { month, origin: window.location.origin }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `BETH-statement-${month}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download the PDF'); }
  };
  return (
    <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800 space-y-4" data-testid="bank-statement-panel">
      <style>{PRINT_CSS}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-400" /> Account statement</h3>
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => setMonth(e.target.value)} className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="statement-month-input" />
          <button onClick={download} disabled={!s} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 rounded-lg text-sm font-semibold flex items-center gap-1" data-testid="statement-pdf-btn"><Download className="w-4 h-4" /> Download PDF</button>
          <button onClick={() => window.print()} disabled={!s} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 rounded-lg text-sm font-semibold flex items-center gap-1" data-testid="statement-print-btn"><Printer className="w-4 h-4" /> Print</button>
        </div>
      </div>
      <EmailToggle token={token} />
      {err && <p className="text-sm text-red-300" data-testid="statement-error">{err}</p>}
      {s && <Statement s={s} />}
    </div>
  );
};
